function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function updateClearAllButtonVisibility() {
  const clearAllBtn = document.querySelector('.facets-clear-all');
  const hasActiveFacets =
    document.querySelector('.active-facets') && document.querySelector('.active-facets').innerText.trim() !== '';
  if (clearAllBtn) {
    clearAllBtn.style.display = hasActiveFacets ? 'block' : 'none';
  }
}

// Ensures only one facet is open at a time
function enableSingleFacetOpen() {
  document.querySelectorAll('.js-filter').forEach((details) => {
    details.removeEventListener('click', handleSingleFacetOpen, true);
    details.addEventListener('click', handleSingleFacetOpen, true);
  });
}

function handleSingleFacetOpen(e) {
  const summary = e.target.closest('summary');
  if (!summary) return;

  const details = summary.closest('.js-filter');
  if (!details) return;

  document.querySelectorAll('.js-filter[open]').forEach((openDetails) => {
    openDetails.removeAttribute('open');
  });

  setTimeout(updateClearAllButtonVisibility, 0);
}

function onKeyUpEscape(event) {
  if (event.key === 'Escape' || event.key === 'Esc') {
    const openDetails = event.currentTarget.querySelectorAll('details[open]');
    openDetails.forEach((details) => {
      details.removeAttribute('open');
    });
    if (document.activeElement) {
      document.activeElement.blur();
    }
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);
    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static renderSection(sectionId, html) {
    const container = document.getElementById(`FacetsContainer-${sectionId}`);
    if (!container) {
      console.warn(`Facets container with id FacetsContainer-${sectionId} not found.`);
      return;
    }
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newContainer = parsed.getElementById(`FacetsContainer-${sectionId}`);
    if (!newContainer) {
      console.warn(`Facets container with id FacetsContainer-${sectionId} not found in new HTML.`);
      return;
    }
    container.innerHTML = newContainer.innerHTML;
    enableSingleFacetOpen();
    updateClearAllButtonVisibility();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    let loaderOverlay = document.getElementById('global-loader-overlay');
    if (!loaderOverlay) {
      loaderOverlay = document.createElement('div');
      loaderOverlay.id = 'global-loader-overlay';
      loaderOverlay.style.position = 'fixed';
      loaderOverlay.style.top = '0';
      loaderOverlay.style.left = '0';
      loaderOverlay.style.width = '100vw';
      loaderOverlay.style.height = '100vh';
      loaderOverlay.style.background = 'rgba(255,255,255,0.85)';
      loaderOverlay.style.zIndex = '99999';
      loaderOverlay.style.display = 'flex';
      loaderOverlay.style.alignItems = 'center';
      loaderOverlay.style.justifyContent = 'center';
      loaderOverlay.innerHTML =
        '<div class="global-loader-spinner" style="width:64px;height:64px;border:8px solid #ccc;border-top:8px solid #1f636f;border-radius:50%;animation:spin 1s linear infinite"></div>';
      document.body.appendChild(loaderOverlay);
      const style = document.createElement('style');
      style.innerHTML = '@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }';
      document.head.appendChild(style);
    } else {
      loaderOverlay.style.display = 'flex';
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;
      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = parsed.getElementById('ProductGridContainer');
    if (!container) {
      console.warn('ProductGridContainer not found in current DOM.');
      const loaderOverlay = document.getElementById('global-loader-overlay');
      if (loaderOverlay) loaderOverlay.style.display = 'none';
      return;
    }
    if (!newGrid) {
      console.warn('ProductGridContainer not found in AJAX response.');
      const loaderOverlay = document.getElementById('global-loader-overlay');
      if (loaderOverlay) loaderOverlay.style.display = 'none';
      console.debug('AJAX response HTML:', html);
      return;
    }
    if (newGrid.innerHTML !== undefined) {
      container.innerHTML = newGrid.innerHTML;
    } else {
      console.warn('newGrid.innerHTML is undefined.');
      const loaderOverlay = document.getElementById('global-loader-overlay');
      if (loaderOverlay) loaderOverlay.style.display = 'none';
      console.debug('newGrid object:', newGrid);
    }
    const loaderOverlay = document.getElementById('global-loader-overlay');
    if (loaderOverlay) loaderOverlay.style.display = 'none';
    const scrollTriggers = container.querySelectorAll('.scroll-trigger');
    if (scrollTriggers) {
      scrollTriggers.forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
    }
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const countElem = parsed.getElementById('ProductCount');
    const count = countElem ? countElem.innerHTML : '';
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    } else {
      console.warn('ProductCount not found in current DOM.');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    } else {
      console.warn('ProductCountDesktop not found in current DOM.');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    if (loadingSpinners) {
      loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });
    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };
    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);
    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }
        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });
    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;
      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));
        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);
        const isTextInput = event.target.getAttribute('type') === 'text';
        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
    enableSingleFacetOpen();
    setTimeout(updateClearAllButtonVisibility, 0);

    const newFacetsContainer = parsedHTML.querySelector('[id^="FacetsContainer-"]');
    if (newFacetsContainer) {
      const sectionIdMatch = newFacetsContainer.id.match(/^FacetsContainer-(.+)$/);
      if (sectionIdMatch) {
        const sectionId = sectionIdMatch[1];
        FacetFiltersForm.renderSection(sectionId, html);
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];
    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!activeFacetsElement || !target) return;
      target.innerHTML = activeFacetsElement.innerHTML;
    });
    FacetFiltersForm.toggleActiveFacets(false);
    updateClearAllButtonVisibility();
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];
    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }
    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');
    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }
    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');
    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }
      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');
    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
      document.querySelectorAll('.js-filter[open]').forEach((openDetails) => {
        openDetails.removeAttribute('open');
      });
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';
      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
enableSingleFacetOpen();

document.addEventListener('shopify:section:load', registerFacetCustomElements);
document.addEventListener('shopify:section:select', registerFacetCustomElements);
document.addEventListener('shopify:section:deselect', registerFacetCustomElements);
document.addEventListener('shopify:block:select', registerFacetCustomElements);
document.addEventListener('shopify:block:deselect', registerFacetCustomElements);

function registerFacetCustomElements() {
  if (!window.customElements.get('facet-filters-form')) {
    window.customElements.define('facet-filters-form', FacetFiltersForm);
  }
  if (!window.customElements.get('price-range')) {
    window.customElements.define('price-range', PriceRange);
  }
  if (!window.customElements.get('facet-remove')) {
    window.customElements.define('facet-remove', FacetRemove);
  }
  FacetFiltersForm.setListeners();
  enableSingleFacetOpen();
}
